import * as React from 'react';
import styled, {css} from 'styled-components';

import {Breakpoints, useTheme} from '@volkswagen-onehub/components-core';
import {MoodGallery} from '../../../d6/components/mood-gallery';

import {MediaAspectRatio} from '../../../../generated/api';
import {MoodGallerySectionModel} from '../../../../generated/core';
import {AuthoringWrapper} from '../../../components/AuthoringWrapper';
import {
    MediaContext,
    MediaInteraction,
    VideoInteraction
} from '../../../context/media/MediaContext';
import {MapTo} from '../../../infrastructure/compatibility/MapTo';
import {C} from '../../../registries/compatibilty';
import {elseUndefined} from '../../../utils/elseUndefined';
import {HeadingElement} from '../elements/HeadingElement';
import {MediaElement} from '../elements/MediaElement';
import {SectionWrapper} from './SectionWrapper';
import {useXOfYLabel} from '../../../components/highlight-feature/HighlightGallery';
import {VideoPlayerV2DisplayMode} from '../../../d6/components/video-player-v2';
import {MoodGalleryMobile} from '../../../d6/components/mood-gallery-mobile';
import {
    MobileCarousel,
    useCarouselState
} from '../../../d6/components/mobile-carousel-v2';
import {GeneralDisclaimerProvider} from '../../../context/disclaimer/GeneralDisclaimerProvider';
import {useFullscreenGalleryLayerService} from '../../../context';
import {ValueFactory} from '../../../utils/contextUtils';
import {ContainerItem} from '../../../infrastructure/ContainerExporter';
import {FullscreenGalleryLayerService} from '../../../context/layer/FullscreenGalleryLayerService';
import {Model} from '@adobe/cq-react-editable-components';
import {makeClip} from '../../../d6/components/helpers';

interface StyledMobileWrapperProps {
    showMobile: boolean;
}

type MoodGalleryItemProps = {
    aspectRatio?: MediaAspectRatio;
    index: number;
    key: number | string;
    onClick?: (e: React.MouseEvent) => void;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    path: string;
    sizes?: string;
    setInteractionData: (data: MediaInteraction) => void;
    useParentAspectRatio?: boolean;
    isInLayer?: boolean;
    isImage?: boolean;
};

export type MoodGalleryItemConfig = {
    mediaItems: ContainerItem[];
    interactionDataFn: (data: MediaInteraction) => void;
    cqPath: string;
    galleryLayerService: FullscreenGalleryLayerService;
    aspectRatio?: MediaAspectRatio;
    activeItemIndex?: number;
    ariaDescribedById?: string;
};

export const StyledMobileWrapper = styled.div<StyledMobileWrapperProps>`
    display: block;

    @media (min-width: ${Breakpoints.b960}px) {
        display: ${props => (props.showMobile ? 'block' : 'none')};
    }
`;
StyledMobileWrapper.displayName = 'StyledMobileWrapper';

const StyledTabletWrapper = styled.div`
    display: none;

    @media (min-width: ${Breakpoints.b960}px) {
        display: block;
    }
`;
StyledTabletWrapper.displayName = 'StyledTabletWrapper';

// remove extra whitespace under animation element
const animationElementAlignment = css`
    .animationElement {
        margin-bottom: -4px;
    }
`;

const StyledMoodGalleryAuthor = styled.div`
    .mediaElement.cq-Editable-dom {
        padding-bottom: 0;
    }

    .imageElement.cq-Editable-dom {
        margin-bottom: 0;
    }

    ${animationElementAlignment};
`;
StyledMoodGalleryAuthor.displayName = 'StyledMoodGalleryAuthor';

const StyledMoodGallery = styled.div`
    ${animationElementAlignment};
`;
StyledMoodGallery.displayName = 'StyledMoodGallery';

// the sizes attribute for media elements in desktop viewports
const mediaSizes: {[key: number]: string[]} = {
    1: ['100vw'],
    2: ['75vw', '50vw'],
    3: ['66vw', '37vw', '33vw'],
    4: ['66vw', '37vw', '33vw', '25vw'],
    5: ['50vw', '37vw', '25vw', '33vw', '25vw']
};

const StyledItemWrapper = styled.div`
    width: 100%;
    cursor: pointer;
`;

const StyledSrDescription = styled.span`
    ${makeClip}
`;

interface VideoFullscreenHandler {
    showMobileView: boolean;
    setInteractionData(data: MediaInteraction): void;
}

export function renderMoodGalleryItems({
    mediaItems,
    interactionDataFn,
    cqPath,
    galleryLayerService,
    aspectRatio,
    activeItemIndex,
    ariaDescribedById
}: MoodGalleryItemConfig) {
    if (!mediaItems.length) return;
    // Component used to create section and layer items
    const MoodGalleryItem: React.FC<MoodGalleryItemProps> = ({
        aspectRatio: ratio = undefined,
        index,
        onClick,
        onKeyDown,
        path,
        sizes,
        setInteractionData,
        useParentAspectRatio = false,
        isInLayer,
        isImage
    }) => {
        // only images are clickable and only when they are not in the fullscreen layer
        const isClickableImage = isImage && !isInLayer;
        const isFocusable = !activeItemIndex || index === activeItemIndex;
        const tabIndex = isClickableImage ? (isFocusable ? 0 : -1) : undefined;

        return (
            <StyledItemWrapper key={index}>
                <MediaContext.Provider
                    value={{
                        ratio,
                        setInteractionData,
                        sizes,
                        clickableImageData: {
                            tabIndex,
                            onClick,
                            onKeyDown,
                            ariaDescribedById
                        }
                    }}
                >
                    <MediaElement
                        path={path}
                        useParentAspectRatio={useParentAspectRatio}
                    />
                </MediaContext.Provider>
            </StyledItemWrapper>
        );
    };

    const isMediaTypeImage = (model: Model): boolean =>
        model.mediaType === 'IMAGE';

    const mediaItemIndexMap = mediaItems.reduce(
        (acc, item, index) => {
            acc[item.key] = index; // Store the index of each item in the map
            return acc;
        },
        {} as Record<string, number>
    );

    // Creating layer items for each item of the section with media type 'IMAGE'
    const layerItems: JSX.Element[] = mediaItems
        .filter(({model}) => isMediaTypeImage(model))
        .map(item => (
            <MoodGalleryItem
                key={item.key}
                index={mediaItemIndexMap[item.key]}
                path={item.key}
                setInteractionData={interactionDataFn}
                sizes="100vw"
                useParentAspectRatio
                isInLayer
            />
        ));

    // Creating section items consisting of all media types
    // Only image items receive an onClick & Enter key handler to open the layer items
    return mediaItems.map(({key, model}, index) => (
        <MoodGalleryItem
            key={key}
            index={index}
            isImage={isMediaTypeImage(model)}
            path={key}
            setInteractionData={interactionDataFn}
            sizes={mediaSizes[mediaItems.length][index]}
            aspectRatio={aspectRatio}
            onClick={
                isMediaTypeImage(model)
                    ? event =>
                          handleMoodGalleryItemClick(
                              cqPath,
                              galleryLayerService,
                              event,
                              key,
                              layerItems,
                              mediaItems
                          )
                    : undefined
            }
            onKeyDown={
                isMediaTypeImage(model)
                    ? event =>
                          event.key === 'Enter' &&
                          handleMoodGalleryItemClick(
                              cqPath,
                              galleryLayerService,
                              event,
                              key,
                              layerItems,
                              mediaItems
                          )
                    : undefined
            }
        />
    ));
}

export const getCorrectIndex = (
    elements: JSX.Element[],
    items: Omit<ContainerItem, 'model'>[],
    key: string
): number => {
    const indexInAllMediaItems = items.findIndex(
        mediaItem => mediaItem.key === key
    );

    return elements.findIndex(
        element => element.props.path === items[indexInAllMediaItems].key
    );
};

export const getContextData = (cqPath: string) => {
    const modelContextValueFactory = new ValueFactory<string>();
    return modelContextValueFactory.create(cqPath);
};

export async function handleMoodGalleryItemClick(
    cqPath: string,
    galleryLayerService: FullscreenGalleryLayerService,
    e: React.MouseEvent | React.KeyboardEvent,
    key: string,
    elements: JSX.Element[],
    items: ContainerItem[]
) {
    e.preventDefault();

    await galleryLayerService?.openLightbox?.(
        getContextData(cqPath),
        elements,
        items,
        getCorrectIndex(elements, items, key)
    );
}

function useVideoFullscreenHandler(): VideoFullscreenHandler {
    const [showMobileView, setShowMobileView] = React.useState(false);
    const setInteractionData = React.useCallback(
        ({data, type}: VideoInteraction) => {
            if (type === 'VIDEO') {
                const showMobile =
                    data.displayMode === VideoPlayerV2DisplayMode.FULLSCREEN;
                if (showMobileView !== showMobile) {
                    setShowMobileView(showMobile);
                }
            }
        },
        [showMobileView]
    );

    return {showMobileView, setInteractionData};
}

export function createAltTextList(mediaItems: ContainerItem[]) {
    return mediaItems.map(
        item =>
            item.model.cqItems.image?.altText ??
            item.model.cqItems.video?.title ??
            item.model.cqItems.animation?.altText
    );
}

function InternalMoodGallerySection(
    props: MoodGallerySectionModel
): JSX.Element | null {
    const {
        anchorId,
        mboxId,
        translations,
        contentLabels,
        cqItems,
        contentId: sectionId,
        contentName,
        tagTypeBroken,
        cqPath
    } = props;
    const [activeItem, handleSlideChange] = useCarouselState(0);
    const {showMobileView, setInteractionData} = useVideoFullscreenHandler();
    const galleryLayerService = useFullscreenGalleryLayerService();
    const {direction} = useTheme();
    const mediaItems = Object.entries(cqItems)
        .filter(([, model]) => model.mediaType)
        .map(
            ([key, model]): ContainerItem => ({
                key,
                model
            })
        );
    const claim: JSX.Element | undefined = elseUndefined(
        C.isInEditor() || !cqItems.heading.empty,
        <HeadingElement path="heading" style="H1" order="H2" />
    );

    const ariaDescribedById = `${sectionId}-sr-only`;

    const moodGallery = (
        <MoodGallery claim={claim}>
            {renderMoodGalleryItems({
                mediaItems,
                interactionDataFn: setInteractionData,
                cqPath,
                galleryLayerService,
                ariaDescribedById
            })}
        </MoodGallery>
    );

    const getXOfYLabel = useXOfYLabel(
        translations['carousel.slideXOfYLabel'],
        direction
    );

    // for an author display only desktop version since some items in carousel could be hidden and author would not be able to reach them
    if (C.isInEditor()) {
        return (
            <StyledMoodGalleryAuthor id={anchorId}>
                <AuthoringWrapper
                    anchorId={anchorId}
                    title="S105 Mood Gallery Section"
                    bgColor={AuthoringWrapper.BG_COLOR_SECTION}
                    tagTypeBroken={tagTypeBroken}
                >
                    {moodGallery}
                </AuthoringWrapper>
            </StyledMoodGalleryAuthor>
        );
    }

    return (
        <GeneralDisclaimerProvider displayTypes={['T3_SECTION_BASED']}>
            <SectionWrapper
                mboxId={mboxId}
                anchorId={anchorId}
                sectionId={sectionId}
                contentLabels={contentLabels}
                contentName={contentName}
                className="vwa-cms-mood-gallery-module"
            >
                <StyledSrDescription id={ariaDescribedById}>
                    {translations['videoElement.enableFullScreenButtonTitle']}
                </StyledSrDescription>
                <StyledTabletWrapper>{moodGallery}</StyledTabletWrapper>
                <StyledMobileWrapper showMobile={showMobileView}>
                    <MoodGalleryMobile claim={claim}>
                        <MobileCarousel
                            carouselId={`${sectionId}-carousel`}
                            altTextList={createAltTextList(mediaItems)}
                            activeSlideIndex={activeItem}
                            onSlideChange={handleSlideChange}
                            defaultPaginationItemLabel={
                                translations['moodGallery.carouselItemLabel']
                            }
                            getXOfYLabel={getXOfYLabel}
                            galleryLabel={translations['carousel.galleryLabel']}
                            paginationLabel={
                                translations['carousel.gallerySlidesLabel']
                            }
                        >
                            {renderMoodGalleryItems({
                                aspectRatio: 'r_3_4',
                                mediaItems,
                                interactionDataFn: setInteractionData,
                                cqPath,
                                galleryLayerService,
                                activeItemIndex: activeItem,
                                ariaDescribedById
                            })}
                        </MobileCarousel>
                    </MoodGalleryMobile>
                </StyledMobileWrapper>
            </SectionWrapper>
        </GeneralDisclaimerProvider>
    );
}

export const MoodGallerySection = MapTo(
    'vwa-ngw18/components/editorial/sections/moodGallerySection',
    InternalMoodGallerySection
);
